import { changeDocumentLanguage, LANGUAGE_CODE } from '@healthhub/react-ui';
import i18n from 'i18next';
import type { ChainedBackendOptions } from 'i18next-chained-backend';
import Backend from 'i18next-chained-backend';
import HttpApi from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

import fallbackResource from '@shared/utils/file/generated-i18n/fallback.json';
import {
  AsyncLocalStorageBackend,
  getI18nHttpBackendOptions,
  I18N_CACHE_PREFIX,
  removeInvalidI18nCache,
} from '@shared/utils/i18n';

const supportedLngs = {
  [LANGUAGE_CODE.en]: 'English',
  [LANGUAGE_CODE.ms]: 'Malay',
  [LANGUAGE_CODE.ta]: 'Tamil',
  [LANGUAGE_CODE.zh]: 'Chinese',
};

// Registered before init() so the languageChanged that init emits for the
// initial language still sets the document language.
i18n.on('languageChanged', (lang) => {
  changeDocumentLanguage(lang);
});

// Tracks which lng|ns pairs already had a background refresh triggered, so
// each bundled namespace is only reloaded once, the first time it's used.
const syncedNamespaces = new Set<string>();

const originalHasLoadedNamespace = i18n.hasLoadedNamespace.bind(i18n);
i18n.hasLoadedNamespace = (ns, options = {}) => {
  const loaded = originalHasLoadedNamespace(ns, options);
  if (loaded) {
    const lngOption = Array.isArray(options.lng) ? options.lng[0] : options.lng;
    const lng = lngOption || i18n.resolvedLanguage || i18n.language;
    const syncKey = `${lng}|${ns}`;
    if (lng && fallbackResource[lng]?.[ns] && !syncedNamespaces.has(syncKey)) {
      syncedNamespaces.add(syncKey);
      queueMicrotask(() => i18n.reloadResources(lng, ns));
    }
  }
  return loaded;
};

// Must run before init(): AsyncLocalStorageBackend is the primary backend, so a
// corrupted cache entry would otherwise block that namespace from loading.
removeInvalidI18nCache();

i18n
  .use(initReactI18next)
  .use(Backend)
  .init<ChainedBackendOptions>({
    partialBundledLanguages: true,
    resources: fallbackResource,
    backend: {
      backends: [
        AsyncLocalStorageBackend, // primary
        HttpApi, // backend api
      ],
      cacheHitMode: 'refreshAndUpdateStore', // tries to refresh cache by loading from the next backend, updates the cache
      backendOptions: [
        { expirationTime: 7 * 24 * 60 * 60 * 1000, prefix: I18N_CACHE_PREFIX },
        getI18nHttpBackendOptions(),
      ],
    },
    fallbackLng: LANGUAGE_CODE.en,
    fallbackNS: 'translation', // fallback translation file
    interpolation: {
      escapeValue: false,
    },
    supportedLngs: Object.keys(supportedLngs), // only allow supported languages to be detected
    debug: import.meta.env.VITE_REACT_APP_ENABLE_LOG === 'true',
    maxRetries: 1,
    react: {
      bindI18nStore: 'added',
    },
  });

export { i18n };
